import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, X, Mail, Play, ArrowRight } from "lucide-react";
import { SectionHeading, CTAButton } from "./primitives";
import { Reveal } from "../motion/Reveal";
import { track } from "../../lib/analytics";
import { fireConversion } from "../../lib/conversions";
import { CohortEnquiry } from "./CohortEnquiry";
import { FilmModal } from "./FilmModal";
import { footer, media } from "../../content/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmt = (a, c) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: (c || "aud").toUpperCase(), maximumFractionDigits: Number.isInteger(a) ? 0 : 2 }).format(a);

const PayPalButton = ({ onClick, loading, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    disabled={loading}
    className="mt-3 w-full flex items-center justify-center gap-2 rounded-sm border border-[#003087] bg-[#ffc439] py-3.5 font-display font-bold text-[#003087] text-sm hover:bg-[#f3b72c] transition-colors disabled:opacity-60"
  >
    {loading ? (
      <Loader2 className="h-4 w-4 animate-spin" />
    ) : (
      <>
        <span className="italic">Pay<span className="text-[#009cde]">Pal</span></span>
        <span className="font-medium text-[#003087]/80">— Pay with PayPal</span>
      </>
    )}
  </button>
);

const labels = {
  otp_cert_full_discount: "OTP Professional Certificate — full payment",
  otp_cert_plan_monthly: "OTP Professional Certificate — 4-month plan",
};

const APPLY_URL = "https://apply.axiomcollege.com.au/";
const OTP_LIST_PRICE = 9999;
const DIPLOMA_LIST_PRICE = 14999;

export const Enrolment = () => {
  const [packages, setPackages] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);
  const [modalPlan, setModalPlan] = useState(null); // { lookup_key, provider }
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackError, setAckError] = useState("");
  const [filmOpen, setFilmOpen] = useState(false);
  const hasFilm = Boolean(media.heroFilmUrl);

  useEffect(() => {
    axios.get(`${API}/payments/packages`).then((r) => {
      const map = {};
      r.data.forEach((p) => (map[p.lookup_key] = p));
      setPackages(map);
    }).catch(() => {});
  }, []);

  const openCheckout = (lookup_key, provider = "stripe") => {
    setEmail("");
    setEmailError("");
    setAcknowledged(false);
    setAckError("");
    setModalPlan({ lookup_key, provider });
  };

  const checkout = async (lookup_key, provider, emailValue) => {
    const key = `${lookup_key}:${provider}`;
    setLoadingKey(key);
    const pkg = packages[lookup_key] || {};
    track("payment_checkout_started", {
      lookup_key,
      provider,
      value: pkg.amount,
      currency: pkg.currency || "aud",
      item_name: labels[lookup_key] || "Operations Transformation Program",
    });
    fireConversion("begin_checkout", {
      user: { email: emailValue },
      value: pkg.amount,
      currency: (pkg.currency || "AUD").toUpperCase(),
    });
    try {
      const endpoint = provider === "paypal" ? "paypal/checkout" : "payments/checkout";
      const { data } = await axios.post(`${API}/${endpoint}`, {
        lookup_key,
        origin_url: window.location.origin,
        email: emailValue,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoadingKey(null);
      toast.error("We couldn't start your checkout. Please try again or contact us if it persists.");
    }
  };

  const confirmEmail = () => {
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (!acknowledged) {
      setAckError("Please confirm you've read the pre-enrolment information before continuing.");
      return;
    }
    const { lookup_key, provider } = modalPlan;
    setModalPlan(null);
    checkout(lookup_key, provider, value);
  };

  useEffect(() => {
    if (!modalPlan) return;
    const onKey = (e) => { if (e.key === "Escape") setModalPlan(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalPlan]);


  const otpFullDisc = packages.otp_cert_full_discount;  // $9,999 — Certificate one-time payment
  const otpPlan = packages.otp_cert_plan_monthly;       // $2,500/mo — Certificate 4-month plan

  return (
    <section id="enrol" data-testid="enrolment-section" className="bg-white py-24 lg:py-32 border-t border-hair">
      <div className="container-page">
        <SectionHeading eyebrow="Secure Your Place">
          Join the next monthly intake.
        </SectionHeading>
        <Reveal delay={0.1}>
          <p className="mt-6 text-slate-ink text-base lg:text-lg leading-relaxed max-w-2xl">
            Two ways to enrol: the <strong className="text-navy">OTP Professional Program</strong> with a
            Program Completion Certificate, or the <strong className="text-navy">OTP plus the nationally
            recognised Diploma of Leadership and Management (BSB50420)</strong>, delivered and assessed by
            Axiom College (RTO 40489). A Pre-Training Review confirms your fit before you begin.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8 items-stretch max-w-5xl">
          {/* Proposition 1 — OTP Professional Certificate (non-accredited) */}
          <Reveal delay={0.06} className="border border-navy/20 rounded-sm p-8 lg:p-9 relative flex flex-col bg-white">
            <p className="eyebrow text-navy">OTP Professional Certificate</p>
            <div className="mt-4 flex items-baseline gap-2 flex-wrap">
              <span className="font-display font-extrabold text-navy text-4xl lg:text-5xl tracking-tight">
                {otpFullDisc ? fmt(otpFullDisc.amount, otpFullDisc.currency) : "AUD $9,999"}
              </span>
              <span className="text-slate-ink text-sm">one-time</span>
            </div>
            <p className="mt-1 text-slate-ink text-xs">
              Or pay over 4 months at {otpPlan ? fmt(otpPlan.amount, otpPlan.currency) : "AUD $2,500"}/mo.
            </p>
            <p className="mt-3 text-slate-ink text-sm">
              The full Operations Transformation Program with an{" "}
              <strong className="text-navy">OTP Completion Certificate</strong> — a practical, non-accredited
              credential you can start today.
            </p>
            <ul className="mt-6 space-y-3 flex-1">
              {[
                "Complete transformation system & simulation access",
                "Operations Transformation Program Completion Certificate",
                "Extend to earn the nationally recognised Diploma for an additional $5,000",
                "Start immediately — enrol and pay securely online",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-navy">
                  <Check className="h-4 w-4 text-teal mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <CTAButton
              variant="outlineDark"
              className="mt-8 w-full"
              data-testid="pay-full"
              onClick={() => openCheckout("otp_cert_full_discount")}
              disabled={loadingKey === "otp_cert_full_discount:stripe"}
            >
              {loadingKey === "otp_cert_full_discount:stripe" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enrol now"}
            </CTAButton>
            <PayPalButton
              testid="pay-full-paypal"
              loading={loadingKey === "otp_cert_full_discount:paypal"}
              onClick={() => openCheckout("otp_cert_full_discount", "paypal")}
            />
            <div className="mt-5 pt-5 border-t border-hair">
              <p className="text-sm text-navy font-display font-semibold">
                Prefer to spread it?{" "}
                <span className="text-slate-ink font-normal">
                  {otpPlan ? fmt(otpPlan.amount, otpPlan.currency) : "AUD $2,500"}/mo × 4
                </span>
              </p>
              <CTAButton
                variant="outlineDark"
                className="mt-3 w-full"
                data-testid="pay-plan"
                onClick={() => openCheckout("otp_cert_plan_monthly")}
                disabled={loadingKey === "otp_cert_plan_monthly:stripe"}
              >
                {loadingKey === "otp_cert_plan_monthly:stripe" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start the 4-month plan"}
              </CTAButton>
              <PayPalButton
                testid="pay-plan-paypal"
                loading={loadingKey === "otp_cert_plan_monthly:paypal"}
                onClick={() => openCheckout("otp_cert_plan_monthly", "paypal")}
              />
            </div>
          </Reveal>

          {/* Proposition 2 — OTP + Nationally Recognised Diploma (accredited, via Axiom) — FEATURED / PREFERRED */}
          <Reveal delay={0.12} className="border-2 border-gold rounded-sm p-8 lg:p-9 relative flex flex-col bg-offwhite shadow-lg">
            <span className="absolute -top-3 left-8 bg-gold text-[#0C1B2E] text-[10px] font-display font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-sm">
              Recommended
            </span>
            <p className="eyebrow text-gold">OTP + Nationally Recognised Diploma</p>
            <div className="mt-4 flex items-baseline gap-2 flex-wrap">
              <span className="font-display font-extrabold text-navy text-4xl lg:text-5xl tracking-tight">
                {fmt(DIPLOMA_LIST_PRICE, "aud")}
              </span>
              <span className="text-slate-ink text-sm">total course fee</span>
            </div>
            <p className="mt-1 text-slate-ink text-xs">
              Flexible payment options arranged at enrolment.
            </p>
            <p className="mt-3 text-slate-ink text-sm">
              The full Operations Transformation Program plus the{" "}
              <strong className="text-navy">Diploma of Leadership and Management (BSB50420)</strong> — training,
              assessment and certification delivered and issued by Axiom College (RTO 40489).
            </p>
            <ul className="mt-6 space-y-3 flex-1">
              {[
                "Nationally recognised BSB50420 qualification",
                "Complete transformation system & simulation access",
                "Full enrolment process (USI, LLND check & written agreement)",
                "Assessed during the program by Axiom College",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-navy">
                  <Check className="h-4 w-4 text-gold mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <CTAButton
              variant="gold"
              as="a"
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 w-full"
              data-testid="enrol-diploma"
              onClick={() => track("enrol_diploma_click", { location: "enrolment", option: "diploma" })}
            >
              Apply via Axiom <ArrowRight className="h-4 w-4" />
            </CTAButton>
            <p className="mt-4 text-xs text-slate-ink/70 leading-relaxed">
              Enrolment and payment for the accredited qualification are completed through Axiom's secure
              application system. No more than $1,500 is collected before your training starts, with the
              balance invoiced progressively as you complete your units.
            </p>
          </Reveal>
        </div>

        <CohortEnquiry />

        {hasFilm && (
          <Reveal delay={0.05}>
            <div className="mt-12 grid md:grid-cols-2 gap-8 lg:gap-12 items-center rounded-sm border border-hair bg-offwhite p-6 lg:p-8">
              <button
                type="button"
                data-testid="enrol-watch-film"
                onClick={() => { track("watch_film_click", { location: "enrolment" }); setFilmOpen(true); }}
                className="group block w-full text-left focus-ring rounded-sm"
                aria-label="Watch the film (3 minutes)"
              >
                <div className="relative aspect-video rounded-sm overflow-hidden border border-hair shadow-lg">
                  <img
                    src={media.heroFilmPoster}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#0C1B2E]/40 group-hover:bg-[#0C1B2E]/25 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="relative flex h-16 w-16 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-teal/60 hero-play-pulse" />
                      <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-teal text-white shadow-lg group-hover:bg-[#08929f] transition-colors duration-300">
                        <Play className="h-6 w-6 fill-current ml-0.5" />
                      </span>
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-t from-[#0C1B2E] via-[#0C1B2E]/70 to-transparent">
                    <span className="text-white font-display font-semibold text-sm tracking-tight">Watch the film</span>
                    <span className="text-white/75 text-xs font-display tracking-wide">3 MIN · SOUND ON</span>
                  </div>
                </div>
              </button>
              <div>
                <p className="eyebrow text-teal">Still weighing it up?</p>
                <h3 className="mt-3 font-display font-extrabold text-navy text-2xl lg:text-3xl tracking-tight leading-tight">
                  See the transformation before you enrol.
                </h3>
                <p className="mt-4 text-slate-ink text-sm lg:text-base leading-relaxed">
                  Take three minutes to watch the full film — the operating problem, the system, and the
                  results — then decide with confidence.
                </p>
                <button
                  type="button"
                  data-testid="enrol-watch-film-link"
                  onClick={() => { track("watch_film_click", { location: "enrolment_link" }); setFilmOpen(true); }}
                  className="mt-5 inline-flex items-center gap-2 text-teal font-display font-semibold text-sm hover:underline focus-ring rounded-sm"
                >
                  <Play className="h-4 w-4 fill-current" /> Watch the 3-minute film
                </button>
              </div>
            </div>
          </Reveal>
        )}

        <div className="mt-8 flex items-center gap-2 text-slate-ink text-xs">
          <ShieldCheck className="h-4 w-4 text-teal" />
          Secure payment by Stripe or PayPal. Not sure yet?{" "}
          <button onClick={() => document.getElementById("prospectus")?.scrollIntoView({ behavior: "smooth" })}
            className="text-teal font-semibold hover:underline">Request the prospectus or book your Pre-Training Review first.</button>
        </div>
      </div>

      {modalPlan && (
        <div
          data-testid="email-capture-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setModalPlan(null)}
        >
          <div className="absolute inset-0 bg-navy/80 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white rounded-sm border border-hair p-8 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Enter your email to continue to payment"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="email-capture-close"
              onClick={() => setModalPlan(null)}
              className="absolute top-4 right-4 text-slate-ink hover:text-navy"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-teal/10">
              <Mail className="h-5 w-5 text-teal" />
            </div>
            <p className="eyebrow text-teal mt-5">{labels[modalPlan.lookup_key]}</p>
            <h3 className="mt-2 font-display font-extrabold text-navy text-xl tracking-tight">
              Where should we send your confirmation?
            </h3>
            <p className="mt-2 text-slate-ink text-sm leading-relaxed">
              Enter your email to continue to secure payment. We'll email your receipt and hold your place if you don't finish.
            </p>
            <label htmlFor="checkout-email" className="sr-only">Email address</label>
            <input
              id="checkout-email"
              data-testid="email-capture-input"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") confirmEmail(); }}
              placeholder="you@company.com"
              className="mt-5 w-full rounded-sm border border-hair px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus-ring"
            />
            {emailError && (
              <p data-testid="email-capture-error" className="mt-2 text-[#E8654F] text-xs">{emailError}</p>
            )}
            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="pretraining-ack"
                className="mt-0.5 h-4 w-4 accent-[#0AADBD] shrink-0"
                checked={acknowledged}
                onChange={(e) => { setAcknowledged(e.target.checked); setAckError(""); }}
              />
              <span className="text-slate-ink text-xs leading-relaxed">
                I have read the{" "}
                <button type="button" onClick={() => { setModalPlan(null); document.getElementById("learner-info")?.scrollIntoView({ behavior: "smooth" }); }} className="text-teal font-semibold hover:underline">pre-enrolment information</button>
                {" "}and{" "}
                <a href={footer.contact.studentHandbookUrl} target="_blank" rel="noopener noreferrer" className="text-teal font-semibold hover:underline">Axiom's Policies &amp; Procedures</a>, and I understand the entry requirements.
              </span>
            </label>
            {ackError && (
              <p data-testid="pretraining-ack-error" className="mt-2 text-[#E8654F] text-xs">{ackError}</p>
            )}
            <CTAButton
              className="mt-5 w-full"
              data-testid="email-capture-continue"
              onClick={confirmEmail}
              withArrow
            >
              Continue to secure payment
            </CTAButton>
            <p className="mt-3 text-slate-ink/70 text-[11px] text-center">
              You'll complete payment securely with {modalPlan.provider === "paypal" ? "PayPal" : "Stripe"}.
            </p>
          </div>
        </div>
      )}

      <FilmModal open={filmOpen} onClose={() => setFilmOpen(false)} src={media.heroFilmUrl} />
    </section>
  );
};
